import React, { useState } from 'react';
import './App.css';
import { db } from './firebaseConfig';
import { addDoc, collection } from 'firebase/firestore';
import Modal from './Modal/Modal.js';

const feedbackOptions = [
  {
    value: 'General Feedback',
    title: 'Share feedback',
    description: 'Offer an idea, raise a concern, or tell the board what is working well.',
  },
  {
    value: 'Question For The Board',
    title: 'Ask the board',
    description: 'Submit a question for the board to consider at an upcoming meeting.',
  },
];

const faqs = [
  {
    question: 'Is this really anonymous?',
    answer: 'Yes. This form does not ask for your name, email address, unit number, or other identifying details.',
  },
  {
    question: 'When will I receive a response?',
    answer: 'Because submissions are anonymous, the board cannot reply directly. Questions of broad interest may be addressed in a future community update or meeting.',
  },
  {
    question: 'What should I not submit here?',
    answer: 'For emergencies, time-sensitive maintenance, account questions, or issues requiring a direct reply, please use the HOA’s regular contact channel.',
  },
];

function App() {
  const [feedbackType, setFeedbackType] = useState('');
  const [feedbackText, setFeedbackText] = useState('');
  const [website, setWebsite] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!feedbackType || !feedbackText.trim()) {
      setError('Please select a message type and add your message.');
      return;
    }

    setIsSubmitting(true);

    try {
      await addDoc(collection(db, 'submissions'), {
        feedbackType,
        feedbacktext: feedbackText.trim(),
        website,
        timestamp: new Date().toLocaleString('en-US', {
          timeZone: 'America/New_York',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        }),
      });
      setFeedbackType('');
      setFeedbackText('');
      setWebsite('');
      setModalIsOpen(true);
    } catch (submissionError) {
      console.error('Unable to submit feedback:', submissionError);
      setError('Your message could not be sent. Please wait a moment and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectFeedbackType = (value) => {
    setFeedbackType(value);
    document.getElementById('message')?.focus();
  };

  return (
    <div className="site-shell">
      <header className="site-header">
        <a className="brand" href="#top" aria-label="Hidden Creek home">
          <span className="brand-mark" aria-hidden="true">HC</span>
          <span>
            <strong>Hidden Creek</strong>
            <small>of Sebring Homeowners Association</small>
          </span>
        </a>
        <nav aria-label="Main navigation">
          <a href="#how-it-works">How it works</a>
          <a href="/community">Board follow-ups</a>
          <a href="#questions">Questions</a>
          <a className="nav-cta" href="#message">Share your voice</a>
        </nav>
      </header>

      <main id="top">
        <section className="hero">
          <div className="hero-copy">
            <p className="eyebrow">A better neighborhood starts with listening</p>
            <h1>Your voice belongs <em>in the conversation.</em></h1>
            <p className="hero-intro">
              Share a question, concern, or idea with a fellow Hidden Creek community
              member who advocates for HOA transparency, accountability, and compliance.
            </p>
            <div className="hero-actions">
              <a className="primary-button" href="#message">Send a message <span aria-hidden="true">→</span></a>
              <a className="text-link" href="#how-it-works">See how it works</a>
            </div>
          </div>

          <aside className="privacy-card">
            <div className="privacy-icon" aria-hidden="true">✓</div>
            <p className="card-kicker">Your privacy matters</p>
            <h2 className="privacy-headline">
              <span>No names.</span>
              <span>No email.</span>
              <span>No identifying details.</span>
            </h2>
            <p>We only collect the message you choose to share and its category.</p>
            <div className="privacy-rule" />
            <span>Anonymous by design</span>
          </aside>
        </section>

        <section className="topics-section" aria-labelledby="topics-title">
          <div className="section-heading">
            <p className="eyebrow">What’s on your mind?</p>
            <h2 id="topics-title">Choose how you’d like to contribute.</h2>
          </div>
          <div className="topic-grid">
            {feedbackOptions.map((option, index) => (
              <button
                className="topic-card"
                key={option.value}
                type="button"
                onClick={() => selectFeedbackType(option.value)}
              >
                <span className="topic-number">0{index + 1}</span>
                <span className="topic-title">{option.title}</span>
                <span className="topic-description">{option.description}</span>
                <span className="topic-arrow" aria-hidden="true">↗</span>
              </button>
            ))}
          </div>
        </section>

        <section className="message-section" id="message">
          <div className="form-intro">
            <p className="eyebrow light">Anonymous message</p>
            <h2>Say what’s on your mind.</h2>
            <p>
              Thoughtful feedback helps bring community concerns to the board’s attention.
              Please avoid including personal information in your message.
            </p>
            <div className="form-note">
              <span aria-hidden="true">i</span>
              <p><strong>Need a direct response?</strong> Use the HOA’s regular contact method so the board can follow up with you.</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="feedback-form" noValidate>
            <div className="form-honeypot" aria-hidden="true">
              <label htmlFor="contact-website">Leave this field empty</label>
              <input
                id="contact-website"
                name="contact-website"
                type="text"
                value={website}
                onChange={(event) => setWebsite(event.target.value)}
                tabIndex="-1"
                autoComplete="off"
              />
            </div>
            <div className="field">
              <label htmlFor="feedback-type">What would you like to share?</label>
              <select
                id="feedback-type"
                value={feedbackType}
                onChange={(event) => setFeedbackType(event.target.value)}
              >
                <option value="" disabled>Select a message type</option>
                {feedbackOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.title}</option>
                ))}
              </select>
            </div>

            <div className="field">
              <div className="label-row">
                <label htmlFor="feedback-message">Your message</label>
                <span>{feedbackText.length} / 1500</span>
              </div>
              <textarea
                id="feedback-message"
                value={feedbackText}
                onChange={(event) => setFeedbackText(event.target.value)}
                placeholder="Share your question, feedback, or idea here..."
                maxLength="1500"
                rows="8"
              />
            </div>

            {error && <p className="form-error" role="alert">{error}</p>}

            <button className="submit-button" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Sending…' : 'Submit anonymously'}
              {!isSubmitting && <span aria-hidden="true">→</span>}
            </button>
            <p className="form-assurance">
              <span aria-hidden="true">◆</span> No identifying information is collected.
            </p>
          </form>
        </section>

        <section className="process-section" id="how-it-works">
          <div className="section-heading">
            <p className="eyebrow">Simple and transparent</p>
            <h2>What happens after you submit?</h2>
          </div>
          <div className="process-grid">
            <article>
              <span>01</span>
              <h3>You share</h3>
              <p>Send your message without adding your name, email, or contact details.</p>
            </article>
            <article>
              <span>02</span>
              <h3>A neighbor receives it</h3>
              <p>Your message goes to an independent community member—not an HOA representative—who raises community concerns with the board.</p>
            </article>
            <article>
              <span>03</span>
              <h3>Follow-up becomes public</h3>
              <p>After a topic is brought before the board, it may be added to the public follow-up record for community reference.</p>
            </article>
          </div>
        </section>

        <section className="faq-section" id="questions">
          <div className="faq-intro">
            <p className="eyebrow">Good to know</p>
            <h2>Questions before you send?</h2>
            <p>Here are a few details about how this community feedback channel works.</p>
          </div>
          <div className="faq-list">
            {faqs.map((faq, index) => {
              const isOpen = openFaq === index;
              return (
                <div className={`faq-item ${isOpen ? 'open' : ''}`} key={faq.question}>
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? -1 : index)}
                    aria-expanded={isOpen}
                  >
                    <span>{faq.question}</span>
                    <span aria-hidden="true">{isOpen ? '−' : '+'}</span>
                  </button>
                  {isOpen && <p>{faq.answer}</p>}
                </div>
              );
            })}
          </div>
        </section>
      </main>

      <footer>
        <a className="brand footer-brand" href="#top">
          <span className="brand-mark" aria-hidden="true">HC</span>
          <span><strong>Hidden Creek</strong><small>of Sebring Homeowners Association</small></span>
        </a>
        <p>Building a stronger community, one conversation at a time.</p>
        <a href="/community">View board follow-ups →</a>
      </footer>

      <Modal isOpen={modalIsOpen} closeModal={() => setModalIsOpen(false)}>
        <div className="success-mark" aria-hidden="true">✓</div>
        <p className="card-kicker">Message received</p>
        <h2>Thank you for speaking up.</h2>
        <p>Your anonymous message has been sent to the independent community member who manages this feedback channel.</p>
        <button className="modal-button-close" onClick={() => setModalIsOpen(false)}>Done</button>
      </Modal>
    </div>
  );
}

export default App;
